import React, {useState, useEffect} from 'react';
import {styled} from '@mui/material';
import {Modal, Input, designSystemToken} from '@lightricks/react-design-system';
import translate from '@/utils/translate';

export interface RenameProjectModalProps {
  open: boolean;
  onClose: () => void;
  onRename: (newName: string) => Promise<void>;
  currentProjectName?: string;
  isLoading?: boolean;
}

const StyledInput = styled(Input)(() => ({
  'width': '100%',
  '& .MuiInputBase-root': {
    borderRadius: '8px',
    border: `1px solid ${designSystemToken('semantic.bg.neutral')}`,
  },
}));

function RenameProjectModal({
  open,
  onClose,
  onRename,
  currentProjectName = '',
  isLoading = false,
}: RenameProjectModalProps) {
  const [projectName, setProjectName] = useState(currentProjectName);

  useEffect(() => {
    if (open) {
      setProjectName(currentProjectName);
    }
  }, [open, currentProjectName]);

  const handleRename = async () => {
    if (projectName.trim()) {
      await onRename(projectName.trim());
    }
  };

  const handleClose = () => {
    setProjectName('');
    onClose();
  };

  const isRenameDisabled =
    !projectName.trim() ||
    projectName.trim() === currentProjectName ||
    isLoading;

  return (
    <Modal
      open={open}
      handleClose={handleClose}
      showCloseButton
      textAlignment="left"
      footerAlignment="right"
      width="403px"
      header={translate('Rename project')}
      primaryButtonText={translate('Save')}
      primaryButtonProps={{
        disabled: isRenameDisabled,
        isLoading,
      }}
      handlePrimaryButtonClick={handleRename}
    >
      <StyledInput
        autoFocus
        placeholder={translate('Type new name here')}
        value={projectName}
        inputProps={{
          value: projectName,
        }}
        onInputChange={(value: string) => setProjectName(value)}
        onKeyPress={({key}: {key: string}) => {
          if (key === 'Enter' && !isRenameDisabled) {
            handleRename();
          }
        }}
        disabled={isLoading}
      />
    </Modal>
  );
}

export default RenameProjectModal;
